import { useEffect, useRef, useState } from 'react';
import { ChevronDown, Check, Mail } from 'lucide-react';
import { REPLY_TEMPLATES } from '@/data/replyTemplates';

/**
 * ReplyTemplateMenu
 * -----------------
 * One-tap responses for the Bothy Enquiries panel. Renders a dropdown of
 * baked-in Heritage-tone reply templates. Selecting a template does two
 * things at once:
 *
 *   1. Copies the interpolated body to the clipboard (Cmd/Ctrl+V lands
 *      it into whatever mail client the founder prefers).
 *   2. Opens a `mailto:` link with the customer\u2019s email + subject +
 *      body pre-populated \u2014 handy if the default OS mail client is
 *      wired to Gmail via GSuite.
 *
 * `enquiry` is a row from `/api/admin/enquiries` and must contain at
 * least `name`, `email` and `subject`.
 */
export default function ReplyTemplateMenu({ enquiry }) {
  const [open, setOpen] = useState(false);
  const [copiedId, setCopiedId] = useState(null);
  const menuRef = useRef(null);

  // Close on outside click.
  useEffect(() => {
    if (!open) return undefined;
    const onDoc = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, [open]);

  // Close on Esc.
  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open]);

  const firstName = (enquiry?.name || 'there').trim().split(/\s+/)[0] || 'there';

  const interpolate = (body) => body.replace(/\{first_name\}/g, firstName);

  const apply = async (tpl) => {
    const filled = interpolate(tpl.body);
    // 1) copy to clipboard
    try {
      await navigator.clipboard.writeText(filled);
      setCopiedId(tpl.id);
      setTimeout(() => setCopiedId(null), 1600);
    } catch {
      // Fallback: legacy execCommand for old browsers
      try {
        const ta = document.createElement('textarea');
        ta.value = filled;
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
        setCopiedId(tpl.id);
        setTimeout(() => setCopiedId(null), 1600);
      } catch {
        /* silently ignore — the mailto below still works */
      }
    }
    // 2) open mailto with everything pre-filled
    const to = encodeURIComponent(enquiry?.email || '');
    const subjectRaw = enquiry?.subject
      ? `${tpl.subjectPrefix} ${enquiry.subject}`
      : tpl.subjectPrefix;
    const subject = encodeURIComponent(subjectRaw);
    const bodyEnc = encodeURIComponent(filled);
    window.location.href = `mailto:${to}?subject=${subject}&body=${bodyEnc}`;
  };

  return (
    <div className="relative inline-block" ref={menuRef}>
      <button
        type="button"
        data-testid="reply-template-toggle"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-haspopup="menu"
        className="inline-flex items-center gap-1.5 px-3 py-2 text-xs uppercase tracking-[0.18em] border border-[var(--brand-stone)] bg-white hover:border-[var(--brand-olive)] transition-colors"
      >
        <Mail size={13} /> Reply w/ template
        <ChevronDown
          size={13}
          className={`transition-transform duration-200 ${open ? 'rotate-180' : ''}`}
        />
      </button>

      {open && (
        <div
          data-testid="reply-template-menu"
          role="menu"
          className="absolute z-40 mt-2 right-0 min-w-[280px] bg-white border border-[var(--brand-stone)] shadow-lg"
        >
          <div className="px-3 py-2 text-[10px] uppercase tracking-[0.22em] text-[var(--brand-muted)] border-b border-[var(--brand-stone)]">
            Pick a reply &middot; personalised for {firstName}
          </div>
          <ul className="py-1 max-h-[360px] overflow-y-auto">
            {REPLY_TEMPLATES.map((tpl) => {
              const copied = copiedId === tpl.id;
              return (
                <li key={tpl.id}>
                  <button
                    type="button"
                    role="menuitem"
                    data-testid={`reply-template-${tpl.id}`}
                    onClick={() => apply(tpl)}
                    className="w-full flex items-center justify-between gap-3 text-left px-3 py-2.5 text-sm text-[var(--brand-ink)] hover:bg-[var(--brand-parchment)] transition-colors"
                  >
                    <span>{tpl.label}</span>
                    {copied ? (
                      <span className="inline-flex items-center gap-1 text-xs text-[var(--brand-olive)] font-semibold">
                        <Check size={12} /> Copied
                      </span>
                    ) : (
                      <span className="text-[10px] uppercase tracking-[0.18em] text-[var(--brand-muted)]">
                        {tpl.subjectPrefix}
                      </span>
                    )}
                  </button>
                </li>
              );
            })}
          </ul>
          <div className="px-3 py-2 text-[10px] text-[var(--brand-muted)] border-t border-[var(--brand-stone)]">
            Text is copied to clipboard + your mail client opens.
          </div>
        </div>
      )}
    </div>
  );
}
